import { Arc } from '@visx/shape';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import type { IrrMatrix, Verdict } from '../api.js';
import { irrCellLabel } from './chart-tables.js';

// Data-viz primitives for the Deal Analyzer report (report-visualization-spec §3). Pure SVG via visx
// so they render crisp on screen AND in headless-PDF (no canvas rasterization). Every NUMBER is
// engine-owned and passed in; these components only encode it. The Nexus Deal Room mirrors this spec.
//
// PALETTE mirrors the --ggp-* / --band-* CSS tokens in styles.css (the brand is the single source;
// SVG fills can't read CSS vars reliably under headless print, so we restate the hexes here and keep
// the two in lockstep). The one hard brand rule (§1.4): GOLD IS AN ACCENT, NEVER A DATA FILL — it
// appears only as the base-case ring, threshold ticks, and vertex/marker dots.
const PALETTE = {
  navy: '#0D3F59',
  navyDeep: '#0A2E42',
  muted: '#5A7A8A',
  navyTint: '#8FB0BF',
  navyPale: '#B9C9D1',
  charcoal: '#212529',
  cream2: '#EDE9DC',
  gold: '#B8964A',
  white: '#FFFFFF',
  band: {
    strong: '#CDEBD9',
    adequate: '#E6F3EA',
    marginal: '#FBECCB',
    weak: '#F6D6D6',
    unsolved: '#EDE9DC',
  },
  verdict: {
    READY: '#1F7A4D',
    ADVANCE: '#0D3F59',
    WORKSHOP: '#9A7016',
    RESUBMIT: '#9C2B2B',
  },
} as const;

const TAU = Math.PI * 2;
const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));

/** Point on a circle, using the d3 convention (0 rad = 12 o'clock, clockwise positive). */
function polar(cx: number, cy: number, r: number, angleRad: number): { x: number; y: number } {
  return { x: cx + r * Math.sin(angleRad), y: cy - r * Math.cos(angleRad) };
}

const fmtPct = (ratio: number | null | undefined, dp = 1): string =>
  ratio == null || !Number.isFinite(ratio) ? '—' : `${(ratio * 100).toFixed(dp)}%`;

// ── §3.1 Investment Score dial ──────────────────────────────────────────────
// Radial gauge, 0–100, 270° sweep. Navy-family track; the value arc takes the verdict band color.
// Verdict thresholds (35/55/75) are GOLD ticks (the accent). Center holds the hero number + verdict.
const DIAL_START = (-135 * Math.PI) / 180; // 7:30 position
const DIAL_SWEEP = (270 * Math.PI) / 180;
const VERDICT_TICKS = [35, 55, 75];

export function ScoreDial({
  score,
  verdict,
  structural,
  showVerdict = true,
}: {
  score: number | undefined;
  verdict: Verdict | undefined;
  structural?: boolean;
  /**
   * Print the verdict word under the score. Off where the recommendation is already stated nearby —
   * the report's headline band names it directly above this dial, and repeating it reads as a draft
   * rather than a designed page. The arc COLOUR still carries the verdict either way.
   */
  showVerdict?: boolean;
}) {
  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const outer = 92;
  const inner = 72;
  const pct = clamp01((score ?? 0) / 100);
  const valueEnd = DIAL_START + DIAL_SWEEP * pct;
  const arcColor = verdict ? PALETTE.verdict[verdict] : PALETTE.navy;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`Investment score ${score ?? 'pending'} of 100, verdict ${verdict ?? 'pending'}`}
    >
      <Group top={cy} left={cx}>
        {/* Track */}
        <Arc
          innerRadius={inner}
          outerRadius={outer}
          startAngle={DIAL_START}
          endAngle={DIAL_START + DIAL_SWEEP}
          cornerRadius={6}
          fill={PALETTE.cream2}
        />
        {/* Value */}
        {score != null && (
          <Arc
            innerRadius={inner}
            outerRadius={outer}
            startAngle={DIAL_START}
            endAngle={valueEnd}
            cornerRadius={6}
            fill={arcColor}
          />
        )}
      </Group>
      {/* Verdict threshold ticks — gold accent */}
      {VERDICT_TICKS.map((t) => {
        const a = DIAL_START + DIAL_SWEEP * (t / 100);
        const p1 = polar(cx, cy, inner - 2, a);
        const p2 = polar(cx, cy, outer + 4, a);
        return (
          <line
            key={t}
            x1={p1.x}
            y1={p1.y}
            x2={p2.x}
            y2={p2.y}
            stroke={PALETTE.gold}
            strokeWidth={2}
          />
        );
      })}
      {/* Center hero */}
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        fontFamily="var(--font-head)"
        fontWeight={800}
        fontSize={46}
        fill={PALETTE.navy}
      >
        {score ?? '—'}
      </text>
      <text
        x={cx}
        y={cy + 18}
        textAnchor="middle"
        fontFamily="var(--font-body)"
        fontSize={12}
        fill={PALETTE.muted}
      >
        / 100
      </text>
      {showVerdict && (
        <text
          x={cx}
          y={cy + 40}
          textAnchor="middle"
          fontFamily="var(--font-head)"
          fontWeight={700}
          fontSize={13}
          letterSpacing="0.06em"
          fill={arcColor}
        >
          {(verdict ?? 'PENDING').toUpperCase()}
        </text>
      )}
      {structural && (
        <text
          x={cx}
          y={cy + 58}
          textAnchor="middle"
          fontFamily="var(--font-body)"
          fontSize={10}
          fill={PALETTE.gold}
        >
          Value-creation gate
        </text>
      )}
    </svg>
  );
}

// ── §3.2 Category scorecard radar ───────────────────────────────────────────
// 6-axis hexagon, each axis normalized to its own category max. Navy stroke + navy fill @12%, GOLD
// vertex dots (the accent). Concentric rings + spokes in pale navy.
export interface RadarAxis {
  key: string;
  label: string;
  value: number | undefined;
  max: number;
}

export function CategoryRadar({ axes }: { axes: RadarAxis[] }) {
  const size = 300;
  const cx = size / 2;
  const cy = size / 2;
  const R = 96;
  const n = axes.length;
  const angleAt = (i: number): number => (i / n) * TAU; // start at top, clockwise

  const ringLevels = [0.25, 0.5, 0.75, 1];
  const dataPts = axes.map((a, i) => {
    const v = a.value == null ? 0 : clamp01(a.value / a.max);
    return polar(cx, cy, R * v, angleAt(i));
  });
  const polygon = dataPts.map((p) => `${p.x},${p.y}`).join(' ');

  // The axis labels sit OUTSIDE the plot radius, so the viewBox is widened horizontally to hold them.
  // Without this the left and right labels run past the edge and are clipped mid-word — "Financial"
  // rendered as "Financ" on a client-facing scorecard, which is worse than no label at all.
  const pad = 52;
  return (
    <svg
      width="100%"
      viewBox={`${-pad} 0 ${size + pad * 2} ${size}`}
      role="img"
      aria-label="Category scores radar"
      className="radar"
    >
      {/* Rings */}
      {ringLevels.map((lvl) => (
        <polygon
          key={lvl}
          points={axes
            .map((_, i) => {
              const p = polar(cx, cy, R * lvl, angleAt(i));
              return `${p.x},${p.y}`;
            })
            .join(' ')}
          fill="none"
          stroke={PALETTE.navyPale}
          strokeWidth={lvl === 1 ? 1.5 : 1}
          opacity={0.6}
        />
      ))}
      {/* Spokes */}
      {axes.map((_, i) => {
        const p = polar(cx, cy, R, angleAt(i));
        return (
          <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={PALETTE.navyPale} opacity={0.6} />
        );
      })}
      {/* Data polygon */}
      <polygon
        points={polygon}
        fill={PALETTE.navy}
        fillOpacity={0.12}
        stroke={PALETTE.navy}
        strokeWidth={2}
      />
      {/* Vertex dots — gold accent */}
      {dataPts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3.5} fill={PALETTE.gold} />
      ))}
      {/* Axis labels */}
      {axes.map((a, i) => {
        const lp = polar(cx, cy, R + 22, angleAt(i));
        const anchor = Math.abs(lp.x - cx) < 12 ? 'middle' : lp.x > cx ? 'start' : 'end';
        return (
          <text
            key={a.key}
            x={lp.x}
            y={lp.y}
            textAnchor={anchor}
            dominantBaseline="middle"
            fontFamily="var(--font-body)"
            fontSize={10.5}
            fill={PALETTE.muted}
          >
            <tspan fontWeight={600} fill={PALETTE.navy}>
              {a.value == null ? '—' : a.value}
            </tspan>
            <tspan> {a.label}</tspan>
          </text>
        );
      })}
    </svg>
  );
}

// ── §3.3 IRR sensitivity heatmap (rent × exit cap) ──────────────────────────
const BAND_FILL: Record<string, string> = {
  strong: PALETTE.band.strong,
  adequate: PALETTE.band.adequate,
  marginal: PALETTE.band.marginal,
  weak: PALETTE.band.weak,
  unsolved: PALETTE.band.unsolved,
};

const signedPct = (ratio: number): string => {
  const p = ratio * 100;
  return `${p > 0 ? '+' : ''}${p.toFixed(0)}%`;
};

export function IrrHeatmapChart({ matrix }: { matrix: IrrMatrix }) {
  const rows = matrix.rows ?? [];
  if (!rows.length) return null;
  const margin = { top: 28, right: 12, bottom: 30, left: 52 };
  // The cell is the chart's DRAWN size, and now that the SVG is capped at its natural width (below)
  // it is also the RENDERED size — so it has to be chosen, not inherited. At 64 the grid came out
  // visibly under-scaled on a desktop card; 96 puts a 4-column matrix at 448px, sitting alongside the
  // returns bullet's 460px in the same card, with labels at their true 13px.
  const cell = 96;
  const cols = matrix.exitCaps.length;
  const nRows = matrix.rentDeltas.length;
  const width = margin.left + cols * cell + margin.right;
  const height = margin.top + nRows * cell + margin.bottom;

  const xScale = scaleBand<number>({
    domain: matrix.exitCaps,
    range: [0, cols * cell],
  });
  const yScale = scaleBand<number>({
    domain: matrix.rentDeltas,
    range: [0, nRows * cell],
  });

  return (
    <svg
      className="chart-fit"
      width="100%"
      // Never UPSCALE past the drawn size. The viewBox is `cell`-sized, so its width depends on how many
      // exit-cap columns the matrix has — and `width="100%"` turned that into a magnification factor: on
      // the 824px card interior a 4-column grid came out at 2.58× and a 2-column grid at 4.29×, painting
      // 13px cell labels at 33px and 56px. Larger than every heading on the page bar the hero number, and
      // outside canon's type scale — a size chosen by arithmetic rather than by design. `width="100%"`
      // stays so the chart still SHRINKS into a phone; only growth is capped.
      style={{ maxWidth: `${width}px` }}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="IRR sensitivity heatmap"
    >
      <Group top={margin.top} left={margin.left}>
        {rows.map((row) =>
          row.map((c) => {
            const x = xScale(c.exitCap) ?? 0;
            const y = yScale(c.rentDelta) ?? 0;
            return (
              <g key={`${c.rentDelta}-${c.exitCap}`}>
                {/*
                  The per-cell read-out. A native SVG <title> — no library, no JS, no hover state to
                  manage, and it survives a headless print because it draws nothing.

                  ⚠️ This is a POINTER affordance, not the accessible path. The SVG root carries
                  role="img", which makes its whole subtree presentational, so a screen reader never
                  reaches these titles. The complete path is the "View the numbers" table below the
                  chart (#368), which is exactly why that contract exists. Both are fed by
                  `irrCellLabel`/`irrMatrixTable` so they cannot describe a cell differently.
                */}
                <title>{irrCellLabel(c)}</title>
                <rect
                  className={`hm__cell${c.isBaseCase ? ' hm__cell--base' : ''}`}
                  x={x}
                  y={y}
                  width={cell - 3}
                  height={cell - 3}
                  rx={4}
                  fill={BAND_FILL[c.band] ?? PALETTE.band.unsolved}
                  stroke={c.isBaseCase ? PALETTE.gold : 'transparent'}
                  strokeWidth={c.isBaseCase ? 2.5 : 0}
                />
                <text
                  x={x + (cell - 3) / 2}
                  y={y + (cell - 3) / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontFamily="var(--font-body)"
                  fontSize={13}
                  fontWeight={c.isBaseCase ? 700 : 500}
                  fill={PALETTE.charcoal}
                >
                  {fmtPct(c.projectIRR, 1)}
                </text>
              </g>
            );
          }),
        )}
        {/* Column headers (exit caps) */}
        {matrix.exitCaps.map((cap) => (
          <text
            key={cap}
            x={(xScale(cap) ?? 0) + (cell - 3) / 2}
            y={-10}
            textAnchor="middle"
            fontFamily="var(--font-head)"
            fontSize={11}
            fill={PALETTE.muted}
          >
            {fmtPct(cap, 2)}
          </text>
        ))}
        {/* Row headers (rent deltas) */}
        {matrix.rentDeltas.map((rd) => (
          <text
            key={rd}
            x={-10}
            y={(yScale(rd) ?? 0) + (cell - 3) / 2}
            textAnchor="end"
            dominantBaseline="central"
            fontFamily="var(--font-head)"
            fontSize={11}
            fill={PALETTE.navy}
            fontWeight={600}
          >
            {signedPct(rd)}
          </text>
        ))}
        {/* Axis titles */}
        <text
          x={(cols * cell) / 2}
          y={nRows * cell + 22}
          textAnchor="middle"
          fontFamily="var(--font-head)"
          fontSize={11}
          fill={PALETTE.muted}
        >
          Exit cap →
        </text>
      </Group>
      <text
        x={14}
        y={margin.top + (nRows * cell) / 2}
        textAnchor="middle"
        fontFamily="var(--font-head)"
        fontSize={11}
        fill={PALETTE.muted}
        transform={`rotate(-90 14 ${margin.top + (nRows * cell) / 2})`}
      >
        Rent Δ ↓
      </text>
    </svg>
  );
}

// ── §3.4 Returns-band bullet gauge ──────────────────────────────────────────
// Horizontal bullet: graded qualitative bands (weak / amber ≥12% / green ≥18%) behind a navy measure
// bar = projectIRR, with GOLD threshold ticks at 12% and 18%. Surfaces returns we compute but never showed.
export function ReturnsBullet({
  projectIRR,
  equityMultiple,
  hold,
}: {
  projectIRR: number | null | undefined;
  equityMultiple: number | null | undefined;
  hold?: number | null;
}) {
  const width = 460;
  const height = 64;
  const m = { left: 4, right: 4, top: 18, bottom: 16 };
  const barH = 18;
  const irr = projectIRR ?? 0;
  const domainMax = Math.max(0.25, irr * 1.15);
  const x = scaleLinear({ domain: [0, domainMax], range: [m.left, width - m.right] });

  const bands: { from: number; to: number; fill: string }[] = [
    { from: 0, to: 0.12, fill: PALETTE.band.weak },
    { from: 0.12, to: 0.18, fill: PALETTE.band.marginal },
    { from: 0.18, to: domainMax, fill: PALETTE.band.strong },
  ];
  const ticks = [0.12, 0.18];

  return (
    <svg
      className="chart-fit"
      width="100%"
      // Same ceiling as the heatmap: a 460-unit viewBox stretched to the 824px card interior rendered at
      // 1.79×, painting the 14px title at 25px.
      style={{ maxWidth: `${width}px` }}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Project IRR vs return-quality bands"
    >
      {/* Bands */}
      {bands.map((b) => (
        <rect
          key={b.from}
          x={x(b.from)}
          y={m.top}
          width={Math.max(0, x(b.to) - x(b.from))}
          height={barH}
          fill={b.fill}
        />
      ))}
      {/* Measure */}
      {projectIRR != null && (
        <rect
          x={x(0)}
          y={m.top + barH / 2 - 4}
          width={Math.max(0, x(irr) - x(0))}
          height={8}
          rx={2}
          fill={PALETTE.navy}
        />
      )}
      {/* Threshold ticks — gold */}
      {ticks.map((t) => (
        <g key={t}>
          <line
            x1={x(t)}
            y1={m.top - 4}
            x2={x(t)}
            y2={m.top + barH + 4}
            stroke={PALETTE.gold}
            strokeWidth={2}
          />
          <text
            x={x(t)}
            y={m.top + barH + 14}
            textAnchor="middle"
            fontFamily="var(--font-body)"
            fontSize={9.5}
            fill={PALETTE.muted}
          >
            {Math.round(t * 100)}%
          </text>
        </g>
      ))}
      {/* Value label */}
      <text
        x={m.left}
        y={12}
        fontFamily="var(--font-head)"
        fontWeight={800}
        fontSize={14}
        fill={PALETTE.navy}
      >
        {fmtPct(projectIRR, 1)} IRR
      </text>
      <text
        x={width - m.right}
        y={12}
        textAnchor="end"
        fontFamily="var(--font-body)"
        fontSize={11}
        fill={PALETTE.muted}
      >
        {equityMultiple != null ? `${equityMultiple.toFixed(2)}× EM` : ''}
        {hold ? ` · ${hold}-yr hold` : ''}
      </text>
    </svg>
  );
}

// ── §3.7 Data-quality completeness donut ────────────────────────────────────
// Sequential navy fill for computed vs unavailable. The hero is the badge/score (passed as children).
export function CompletenessDonut({
  computed,
  total,
  label,
}: {
  computed: number;
  total: number;
  label?: string;
}) {
  const size = 132;
  const cx = size / 2;
  const cy = size / 2;
  const outer = 56;
  const inner = 40;
  const frac = total > 0 ? clamp01(computed / total) : 0;
  const end = TAU * frac;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`${computed} of ${total} fields computed`}
    >
      <Group top={cy} left={cx}>
        <Arc
          innerRadius={inner}
          outerRadius={outer}
          startAngle={0}
          endAngle={TAU}
          fill={PALETTE.cream2}
        />
        {frac > 0 && (
          <Arc
            innerRadius={inner}
            outerRadius={outer}
            startAngle={0}
            endAngle={end}
            cornerRadius={3}
            fill={PALETTE.navy}
          />
        )}
      </Group>
      <text
        x={cx}
        y={cy - 2}
        textAnchor="middle"
        fontFamily="var(--font-head)"
        fontWeight={800}
        fontSize={22}
        fill={PALETTE.navy}
      >
        {total > 0 ? Math.round(frac * 100) : '—'}
        {total > 0 && <tspan fontSize={12}>%</tspan>}
      </text>
      <text
        x={cx}
        y={cy + 16}
        textAnchor="middle"
        fontFamily="var(--font-body)"
        fontSize={10}
        fill={PALETTE.muted}
      >
        {label ?? 'complete'}
      </text>
    </svg>
  );
}

// ── §3.5 / §3.6 Hurdle micro-bullet (HTML, not SVG) ─────────────────────────
// A compact actual-vs-hurdle bar: navy measure on a cream track, GOLD hurdle tick. Used under KPI
// tiles and in the lender card. `higherIsBetter` flips which side is the pass region (LTC: lower better).
export function HurdleBar({
  value,
  hurdle,
  pass,
}: {
  value: number | null | undefined;
  hurdle: number | null | undefined;
  pass?: boolean | null;
}) {
  if (value == null || hurdle == null) return null;
  const scaleMax = Math.max(value, hurdle) * 1.45 || 1;
  const valPct = clamp01(value / scaleMax) * 100;
  const hurdlePct = clamp01(hurdle / scaleMax) * 100;
  const cls = pass == null ? '' : pass ? ' hurdle--pass' : ' hurdle--fail';
  return (
    <div className={`hurdle${cls}`} aria-hidden="true">
      <div className="hurdle__track">
        <div className="hurdle__fill" style={{ width: `${valPct}%` }} />
        <div className="hurdle__tick" style={{ left: `${hurdlePct}%` }} />
      </div>
    </div>
  );
}
